/**
 * Web component: Campaign teaser (campaign-teaser)
 * -----------------------------------------------------------------------------
 * Campaign Teaser.
 *
 */
import classes from '~/config/classes';
import './campaign-teaser.scss';

import { vueReady } from '~/core/runners';
import timings from '~/config/timings';

import { dateTime } from '~/helpers/general';
import { checkIfActiveNonPrivate } from '~/helpers/teasers';

/**
 * DOM selectors.
 */
const selectors = {
  campaignContainer: '[js-campaign-teaser="content"]',
  contentCounting: '[js-campaign-teaser="contentCounting"]',
  contentDefault: '[js-campaign-teaser="contentDefault"]',
  copyButton: '[js-campaign-teaser="copyButton"]',
  tooltip: '[js-campaign-teaser="tooltip"]',
  promoText: '[js-campaign-teaser="promoText"]',
};

/**
 * Create new <campaign-teaser> custom element.
 */
class CampaignTeaser extends HTMLElement {
  data(component) {
    const campaignContainer = component.querySelector(
      selectors.campaignContainer,
    );
    if (campaignContainer) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.handleTrackingCampaignView(campaignContainer);
            observer.disconnect();
          }
        });
      });

      observer.observe(campaignContainer);
    }

    return {
      selectors: {
        campaignContainer,
        copyButton: component.querySelector(selectors.copyButton),
        promoText: component.querySelector(selectors.promoText),
      },
    };
  }

  /**
   * Component lifecycle - before mount.
   */
  constructor() {
    super();
    this.$data = this.data(this);
    this.teaserUpdated = false;

    // for initial load, read campaign state from local storage and update
    // teaser if necessary
    const { copyButton } = this.$data.selectors;
    if (copyButton) {
      const discountCampaigns = JSON.parse(
        localStorage.getItem('discount-campaigns'),
      );
      this.updateTeaser(discountCampaigns);

      // listen to campaign changes, update teaser content
      cnvs.EventBus.listen('discount-campaign', (campaigns) => {
        this.updateTeaser(campaigns);
      });
    }
  }

  /**
   * Component lifecycle - mounted.
   */
  connectedCallback() {
    this.$data = Object.keys(this.$data).length ? this.$data : this.data(this);

    const { copyButton } = this.$data.selectors;

    if (copyButton) {
      copyButton.addEventListener('click', this.handleCopy);
    }

    let result = dateTime(
      this.dataset.countdownStart,
      this.dataset.countdownEnd,
    );

    if (result) {
      const contentCounting = this.querySelector(selectors.contentCounting);
      const contentDefault = this.querySelector(selectors.contentDefault);

      const countdownInterval = setInterval(() => {
        result = dateTime(
          this.dataset.countdownStart,
          this.dataset.countdownEnd,
        );
        if (!result) {
          contentCounting.classList.add(classes.hidden);
          contentDefault.classList.remove(classes.hidden);
          this.classList.remove(classes.active);
          clearInterval(countdownInterval);
          cnvs.EventBus.emit('campaign:countdown-finished');
        }
      }, 1000);
    }
  }

  /**
   * Copy promo code and toggle tooltip.
   * @param {Event} event - Click event.
   */
  handleCopy(event) {
    if (!event.target.matches(selectors.copyButton)) {
      return;
    }

    const copyButton = event.target;
    const tooltip = event.target.querySelector(selectors.tooltip);

    if (!copyButton) {
      return;
    }

    const code = copyButton.dataset.code;
    navigator.clipboard.writeText(code);

    if (!tooltip) {
      return;
    }

    tooltip.classList.add(classes.active);

    setTimeout(() => {
      tooltip.classList.remove(classes.active);
    }, timings.message);
  }

  /**
   * Update teaser content based on campaign store state (code applied or not).
   * @param {Object} campaigns Can be cnvs.discount-campaigns (localStorage) or
   * object passed in 'discount-campaign' event emitted by discount-campaigns.vue
   */
  updateTeaser(campaigns) {
    this.$data = Object.keys(this.$data).length ? this.$data : this.data(this);

    const { promoText, copyButton } = this.$data.selectors;

    if (!copyButton) {
      return;
    }

    const copyButtonIcon = copyButton.querySelector('svg');
    const copyButtonText = copyButton.querySelector('span:first-child');

    if (checkIfActiveNonPrivate(campaigns)) {
      copyButton.disabled = true;
      copyButtonIcon.style.display = 'none';
      copyButtonText.innerText = campaigns.active.code[0];
      promoText.innerText = promoText.dataset.codeApplied;

      this.teaserUpdated = true;
    } else if (this.teaserUpdated) {
      // reset everything to initial state
      copyButton.disabled = false;
      copyButtonText.innerText = copyButton.dataset.code;
      copyButtonIcon.style.display = 'unset';
      promoText.innerText = promoText.dataset.text;

      this.teaserUpdated = false;
    }
  }

  /**
   * Handle campaign view for dataLayer.
   * @param {Object} teaser - Teaser item.
   */
  handleTrackingCampaignView(campaign) {
    cnvs.EventBus.emit('dataLayer:view_promotion', {
      promotion: {
        promotion_id: campaign.dataset.campaignId
          ? campaign.dataset.campaignId
          : '',
        promotion_name: campaign.dataset.campaignName
          ? campaign.dataset.campaignName
          : '',
        creative_slot: 'campaign-teaser',
        creative_name: 'Campaign Teaser',
      },
    });
  }
}

/**
 * Define web component on Vue initialisation.
 */
vueReady(() => {
  if (customElements.get('campaign-teaser')) {
    return;
  }

  customElements.define('campaign-teaser', CampaignTeaser);
});
